<template>
  <div 
    class="avatar-circle"
    :class="{ 'avatar-clickable': !readonly }"
    :style="avatarStyles"
    @click="handleClick"
  >
    <!-- 1. Отображаем картинку, если передан src и не было ошибки загрузки -->
    <img 
      v-if="src && !isImgError" 
      v-secure-img="src"
      :alt="username"
      class="avatar-img"
      @error="handleImgError"
    />

    <!-- 2. Слой с текстом-заглушкой (инициалы/Гость) -->
    <!-- Класс avatar-text теперь отвечает за плавное исчезновение текста -->
    <div 
      v-else-if="showText" 
      class="avatar-text"
      :class="{ 'hide-on-hover': !readonly }"
    >
      <span>{{ displayText }}</span>
    </div>

    <!-- 3. Слой с иконкой редактирования -->
    <!-- Появляется поверх текста или поверх картинки при наведении (только если не readonly) -->
    <div v-if="!readonly" class="avatar-overlay">
      <svg viewBox="0 0 24 24" width="40%" height="40%" fill="#ffffff">
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Avatar',
  props: {
    // Ссылка на картинку аватарки
    src: {
      type: String,
      default: ''
    },
    username: {
      type: String,
      required: false,
      default: 'User'
    },
    // Режим гостя: выводит "Гость" и делает фон нейтральным
    isGuest: {
      type: Boolean,
      default: false
    },
    // Размер кружка в пикселях (передается числом, например :size="60")
    size: {
      type: Number | String,
      default: 50
    },
    // Показывать ли текст (инициалы или слово Гость) внутри кружка
    showText: {
      type: Boolean,
      default: true
    },
    borderVisible: {
      type: Boolean,
      default: true
    },
    // Флаг: если true — клик заблокирован, эффекта наведения нет
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      // Флаг ошибки загрузки изображения
      isImgError: false
    };
  },
  watch: {
    // Если вдруг src изменится динамически (например, пользователь обновил профиль),
    // сбрасываем ошибку, чтобы попробовать загрузить новую картинку
    src() {
      this.isImgError = false;
    }
  },
  computed: {
    // Формируем текст для отображения (Инициалы или Гость)
    displayText() {
      if (this.isGuest) return 'Гость';
      if (!this.username) return '?';
      
      const words = this.username.trim().split(/\s+/);
      const firstInitial = words[0] ? words[0].charAt(0) : '';
      const secondInitial = words[1] ? words[1].charAt(0) : '';
      
      return (firstInitial + secondInitial).toUpperCase();
    },
    
    // Генерируем цвет фона и цвет текста
    avatarColors() {
      // Если отображается картинка и она загрузилась успешно, 
      // цвет фона делаем прозрачным/нейтральным
      if (this.src && !this.isImgError) {
        return { background: '#f0f0f0', text: 'transparent' };
      }

      // Для гостя используем фиксированный нейтральный цвет
      if (this.isGuest) {
        return { background: 'hsl(0, 0%, 75%)', text: '#ffffff' };
      }

      // Хэшируем имя пользователя для уникального цвета
      let hash = 0;
      for (let i = 0; i < this.username.length; i++) {
        hash = this.username.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      const hue = Math.abs(hash % 360);
      const saturation = 65; 
      const lightness = 55;  
      const textColor = lightness > 65 ? '#2c3e50' : '#ffffff';
      
      return {
        background: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
        text: textColor
      };
    },

    // Объединяем все динамические стили
    avatarStyles() {
      const fontMultiplier = this.isGuest ? 0.22 : 0.36;
      const calculatedFontSize = Math.max(9, Math.round(this.size * fontMultiplier));
      const borderColor = this.borderVisible ? 'var(--textColor)' : 'transparent';
      return {
        width: `${this.size}px`,
        height: `${this.size}px`, // гарантируем пропорции
        minWidth: `${this.size}px`,
        minHeight: `${this.size}px`,
        backgroundColor: this.avatarColors.background,
        borderColor: borderColor,
        color: this.avatarColors.text,
        fontSize: `${calculatedFontSize}px`,
        padding: this.src && !this.isImgError ? '0' : `0 ${Math.round(this.size * 0.05)}px`
      };
    }
  },
  methods: {
    // Срабатывает, если картинка по ссылке не нашлась (404) или упала сеть
    handleImgError() {
      this.isImgError = true;
    },
    handleClick(event) {
      // Излучаем событие наружу только если readonly === false
      if (!this.readonly) {
        this.$emit('click', event);
      }
    }
  }
}
</script>

<style scoped>
.avatar-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid transparent;
  user-select: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
}

/* Стили только для кликабельной аватарки */
.avatar-clickable {
  cursor: pointer;
}

/* Слой затемнения при наведении */
.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  /* transition: opacity 0.2s ease; */
  opacity: 0;
  transform: scale(1.1); /* Иконка изначально чуть увеличена */
  transition: opacity 0.25s ease, transform 0.25s ease;
  z-index: 2;
}

/* При наведении плавно проявляем иконку и возвращаем к нормальному масштабу */
.avatar-clickable:hover .avatar-overlay {
  opacity: 1;
  transform: scale(1);
}

/* При наведении плавно скрываем текст */
.avatar-clickable:hover .avatar-text.hide-on-hover {
  opacity: 0 !important;
  transform: scale(0.8);
}

/* Оболочка для текста */
.avatar-text {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.25s ease, transform 0.25s ease;
  opacity: 1;
  transform: scale(1);
  box-sizing: border-box;
  padding: 0 8%; 
  z-index: 1; /* Текст находится на нижнем слое */
}

.avatar-text span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  text-align: center;
  font-weight: 100;
  font-family: sans-serif;
  text-transform: uppercase;
  /* Буквы будут стоять чуть плотнее, помогая слову уместиться */
  /* letter-spacing: -0.02em; */
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  z-index: 1;
}
</style>